/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import type { ClientRuntimeConfig, UserInfo } from '../types';
import { API_BASE_URL, requestJson } from './httpClient';

export type CustomerServiceUnreadCountResult = {
  has_unread: boolean;
  unread_count: number;
  user_id?: number | null;
  uid?: string | null;
};

const toText = (value: unknown) => String(value || '').trim();

const normalizeUnreadCountResult = (
  value: unknown
): CustomerServiceUnreadCountResult => {
  const rawPayload =
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  const payload =
    rawPayload.data && typeof rawPayload.data === 'object' && !Array.isArray(rawPayload.data)
      ? (rawPayload.data as Record<string, unknown>)
      : rawPayload;
  const unreadCount = Number(payload.unread_count);
  return {
    has_unread: Boolean(payload.has_unread ?? (Number.isFinite(unreadCount) && unreadCount > 0)),
    unread_count: Number.isFinite(unreadCount) && unreadCount > 0 ? Math.floor(unreadCount) : 0,
    user_id:
      typeof payload.user_id === 'number' && Number.isFinite(payload.user_id)
        ? Math.floor(payload.user_id)
        : null,
    uid: toText(payload.uid) || null,
  };
};

export const fetchCustomerServiceUnreadCount = async (
  config: Pick<
    ClientRuntimeConfig,
    | 'customerServiceBaseUrl'
    | 'customerServiceUnreadEndpoint'
    | 'customerServiceUnreadCountEnabled'
  >,
  _user?: Pick<UserInfo, 'uid' | 'phone'> | null
): Promise<CustomerServiceUnreadCountResult> => {
  if (!config.customerServiceUnreadCountEnabled) {
    return { has_unread: false, unread_count: 0, user_id: null, uid: null };
  }

  const payload = await requestJson<unknown>(
    `${API_BASE_URL}/customer-service/unread`,
    { method: 'GET' },
    { suppressUnauthorizedRedirect: true }
  );
  return normalizeUnreadCountResult(payload);
};
